import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield } from 'lucide-react';

const NAV_ITEMS = {
  customer: ['Dashboard', 'My Tickets', 'Service Catalog', 'Knowledge Base', 'Growth Hub', 'Cyber Ops', 'DocForge', 'Remote Support', 'Train', 'Talent Hub', 'Tech Finder', 'Policies', 'Create Ticket', 'Notifications'],
  technician: ['Dashboard', 'Job Queue', 'Remote Support', 'Train', 'Knowledge Base', 'Talent Hub', 'Tech Finder', 'Policies', 'My Profile', 'Notifications'],
  business_admin: ['Dashboard', 'Tickets', 'Service Catalog', 'Knowledge Base', 'Growth Hub', 'Cyber Ops', 'DocForge', 'Cloud Migration', 'SCADA Monitor', 'Marketplace', 'Diagnostics', 'Remote Support', 'Cannabis Compliance', 'METRC Tracking', 'Endpoint Telemetry', 'Talent Hub', 'Compliance', 'Remediation', 'Wix Hub', 'Integrations', 'HIPAA', 'Leaderboard', 'Reports', 'Billing', 'Train', 'Dispatch', 'SLA Monitor', 'Team', 'Departments', 'Organization', 'White Label', 'Notifications'],
  platform_admin: ['Dashboard', 'All Tickets', 'Service Catalog', 'Knowledge Base', 'Growth Hub', 'Cyber Ops', 'DocForge', 'Cloud Migration', 'SCADA Monitor', 'Marketplace', 'Diagnostics', 'Remote Support', 'Cannabis Compliance', 'METRC Tracking', 'Endpoint Telemetry', 'Talent Hub', 'Compliance', 'Remediation', 'Wix Hub', 'Integrations', 'HIPAA', 'Leaderboard', 'Reports', 'Billing', 'Train', 'UAT', 'Dispatch', 'SLA Monitor', 'Users', 'Departments', 'Organizations', 'White Label', 'Analytics', 'Notifications'],
};

// Items that have dedicated routes (navigate directly)
const ROUTE_MAP = {
  'Service Catalog': '/services',
  'Knowledge Base': '/kb',
  'Growth Hub': '/growth',
  'Cyber Ops': '/cyber',
  'White Label': '/whitelabel',
  'Cloud Migration': '/migration',
  'SCADA Monitor': '/scada',
  'Remote Support': '/remote',
  'Cannabis Compliance': '/cannabis',
  'METRC Tracking': '/metrc',
  'Endpoint Telemetry': '/sysmon',
  'Train': '/train',
  'UAT': '/uat',
  'DocForge': '/docforge',
  'Talent Hub': '/talent',
  'Compliance': '/compliance',
  'Remediation': '/remediation',
  'Wix Hub': '/wixhub',
  'Integrations': '/integrations',
  'HIPAA': '/hipaa',
  'Tech Finder': '/techfinder',
  'Policies': '/policies',
  'Marketplace': '/marketplace',
  'Diagnostics': '/diagnostics',
  'Create Ticket': '/tickets/new',
};

// Items handled internally by Dashboard.js
const DASHBOARD_TABS = [
  'Dashboard', 'My Tickets', 'All Tickets', 'Tickets', 'Job Queue',
  'My Profile', 'Users', 'Organizations', 'Organization', 'Team',
  'Dispatch', 'SLA Monitor', 'Analytics', 'Notifications', 'Departments',
  'Leaderboard', 'Reports', 'Billing',
];

export default function Sidebar({ activeTab, onTabChange }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || 'customer';
  const items = NAV_ITEMS[role] || NAV_ITEMS.customer;

  const handleItemClick = (item) => {
    if (ROUTE_MAP[item]) {
      navigate(ROUTE_MAP[item]);
    } else if (DASHBOARD_TABS.includes(item)) {
      navigate(`/dashboard?tab=${encodeURIComponent(item)}`);
    } else {
      onTabChange(item);
    }
  };

  return (
    <aside className="sidebar-dark w-60 min-h-screen flex flex-col shrink-0" data-testid="sidebar">
      <div className="px-5 py-5 flex items-center gap-2.5 border-b border-white/10">
        <div className="w-8 h-8 bg-[#D4A843] rounded-lg flex items-center justify-center shadow-[0_0_18px_rgba(212,168,67,0.45)]">
          <Shield className="w-4 h-4 text-[#0A0A0A]" strokeWidth={2} />
        </div>
        <span className="font-display text-sm tracking-[0.22em] text-white">TECHOPS</span>
      </div>
      <div className="px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="signal-dot" />
          <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
        </div>
        <p className="text-xs text-slate-400 truncate mt-0.5" data-testid="sidebar-email">
          {(user?.email || '').replace(/@bizboogie\.com$/i, '@eigooinc.net')}
        </p>
        <span className="inline-block mt-1.5 text-[10px] font-display tracking-[0.18em] text-[#D4A843] bg-[#D4A843]/15 px-2 py-0.5 rounded">
          {role.replace('_', ' ').toUpperCase()}
        </span>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {items.map((item) => (
          <button
            key={item}
            onClick={() => handleItemClick(item)}
            className={`sidebar-item w-full text-left ${activeTab === item ? 'active' : ''}`}
            data-testid={`sidebar-${item.toLowerCase().replace(/\s/g, '-')}`}
          >
            {item}
          </button>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/10">
        <button
          onClick={logout}
          className="sidebar-item w-full text-left text-red-400 hover:text-red-300"
          data-testid="sidebar-logout"
        >
          Sign Out
        </button>
        <p className="text-[9px] text-slate-500 mt-3 px-2">TechOps — EIGOO Inc</p>
        <a href="https://app.emergent.sh/?via=Eigoo" target="_blank" rel="noopener noreferrer" className="text-[9px] text-slate-400 hover:text-[#D4A843] px-2 mt-1 block">Powered by Emergent</a>
        <a href="https://www.skool.com/signup?ref=e3e2967f8a3846bb87a53db8b592c54e" target="_blank" rel="noopener noreferrer" className="text-[9px] text-slate-400 hover:text-[#D4A843] px-2 mt-0.5 block">Join our Skool</a>
      </div>
    </aside>
  );
}
